import { useEffect, useState } from 'react';
import { doc } from 'firebase/firestore';
import { useAuth } from '../auth/AuthProvider';
import { db } from '../lib/firebase';
import { listenDocument } from '../lib/liveRead';
import type { Company } from '../types/models';

type Features = NonNullable<Company['features']>;
export function useCompanyFeatures() {
  const { user, activeCompanyId } = useAuth();
  const scope = JSON.stringify([user?.uid, activeCompanyId]);
  const [attempt, retry] = useState(0);
  const [state, setState] = useState<{scope: string; features: Features; ready: boolean; error: string | null} | null>(null);
  useEffect(() => {
    let active = true;
    setState(null);
    if (!user || !activeCompanyId) return;
    const stop = listenDocument(doc(db, 'companies', activeCompanyId), snap => {
      if (!active) return;
      setState({scope, features: snap.data()?.features ?? {}, ready: snap.exists(), error: snap.exists() ? null : 'Company settings not found.'});
    }, error => { if (active) setState({scope, features: {}, ready: false, error: error.message}); });
    return () => { active = false; stop(); };
  }, [scope, attempt]);
  const current = state?.scope === scope ? state : null;
  return {
    ready: current?.ready === true,
    contactEmailEnabled: current?.ready === true && current.features.contactEmailEnabled === true,
    additionalChargesEnabled: current?.ready === true && current.features.additionalChargesEnabled !== false,
    accountsQueueEnabled: current?.ready === true && current.features.accountsQueueEnabled !== false,
    error: current?.error ?? null,
    retry: () => retry(n => n + 1),
  };
}
